import type {Either} from '#lit/types/Either.js';
import {isLeft, isRight} from '#lit/types/Either.js';
import type {
  Audience,
  AudienceCategory,
  AudienceTemplate,
  CustomAttributes,
  Tag,
} from '#lit/types/audiences.js';
import {debounce} from '#utils/debounce.js';
import type {AudienceStatus} from '#lit/common/constants/index.js';
import type {
  ErrorCallback,
  SideEffects,
} from '#lit/types/builders/audience_builder/services.js';
import type {DatasetGroup} from '#lit/types/datasets.js';
import type {RequestManagerError} from '../request_manager/errors.js';
import {DuplicatedRequestError} from '../request_manager/errors.js';
import {
  ABRequestKeys,
  ABRequestManager,
} from '../request_manager/request_manager.js';
import type {SerializedBreakdown} from '#lit/types/builders/audience_builder/index.js';

export type AudienceSaveResponse = {
  data: {audience: Audience};
};

export type AudienceRequestBody = {
  name: string;
  description: string;
  tags: Tag[];
  custom_attributes: CustomAttributes;
  query: string;
  treatment: number;
  control: number;
  size: number;
  last_refreshed_size_at: Date | undefined;
  creator: 'user';
  revenue_touched: number;
  status: AudienceStatus | string;
  criteria_graph: object;
  breakdowns: SerializedBreakdown[];
  category: AudienceCategory;
  origin_audience_id: number | undefined;
};

export type AudienceRequestReadOnlyBody = {
  tags: Tag[];
  custom_attributes: CustomAttributes;
};

export type AudienceServiceError = RequestManagerError;

// How long requests are debounce for.
export const DEBOUNCE_MS = 300;

export class AudienceService {
  saveOrUpdate = debounce(this.runSaveOrUpdate, DEBOUNCE_MS);
  requestApproval = debounce(this.runRequestApproval, DEBOUNCE_MS);

  private requestManager = new ABRequestManager();

  private handleSaveOrUpdateFail(
    error: RequestManagerError,
    callback: ErrorCallback<AudienceServiceError>
  ): void {
    console.error(error);
    if (error.name === DuplicatedRequestError.errorName) {
      return;
    }
    callback(error);
  }

  private async runRequest<Response>(
    args: Parameters<ABRequestManager['execute']>,
    sideEffects?: SideEffects<AudienceServiceError, Response>
  ) {
    const response = await this.requestManager.execute<Response>(...args);

    if (sideEffects) {
      if (isLeft(response)) {
        this.handleSaveOrUpdateFail(response.value, sideEffects.onError);
      }

      if (isRight(response)) {
        sideEffects.onSuccess(response.value);
      }
    }

    return response;
  }

  private runSaveOrUpdate(
    execution: {
      key: ABRequestKeys;
      payload: AudienceRequestBody | AudienceRequestReadOnlyBody;
      options?: {
        disallowAbort: boolean;
      };
    },
    sideEffects?: SideEffects<AudienceServiceError, AudienceSaveResponse>
  ): Promise<Either<AudienceServiceError, AudienceSaveResponse>> {
    const {key, payload, options} = execution;
    return this.runRequest([key, payload, options], sideEffects);
  }

  private runRequestApproval(
    payload: AudienceRequestBody | AudienceRequestReadOnlyBody,
    sideEffects?: SideEffects<AudienceServiceError, AudienceSaveResponse>
  ): Promise<Either<AudienceServiceError, AudienceSaveResponse>> {
    return this.runRequest([ABRequestKeys.UPDATE, payload], sideEffects);
  }

  setup(props: {
    audience?: Audience | AudienceTemplate;
    datasetGroup?: DatasetGroup;
  }): void {
    if (props.audience) {
      this.requestManager.setAudience(props.audience);
    }
    if (props.datasetGroup) {
      this.requestManager.setDatasetGroup(props.datasetGroup);
    }
  }
}
